import { Heart, Camera, Plane } from "lucide-react";
import { FadeIn } from "@/components/motion/FadeIn";
import { useSocialPolaroids } from "@/hooks/useSocialPolaroids";
import socialRio1 from "@/assets/social-rio-1.jpg";
import socialRio2 from "@/assets/social-rio-2.jpg";
import socialRio3 from "@/assets/social-rio-3.jpg";
import socialRio4 from "@/assets/social-rio-4.jpg";
import socialRio5 from "@/assets/social-rio-5.jpg";
import socialRio6 from "@/assets/social-rio-6.jpg";

interface Account {
  handle: string;
  href: string;
  description: string;
  label: string;
  icon: typeof Camera;
  stampColor: "red" | "orange";
}

const accounts: Account[] = [
  {
    handle: "@roca.rio_",
    href: "https://instagram.com/roca.rio_",
    description: "Coulisses des excursions, spots cachés et vie carioca au quotidien.",
    label: "Le compte voyage",
    icon: Camera,
    stampColor: "red",
  },
  {
    handle: "@meninos.dobrasil",
    href: "https://instagram.com/meninos.dobrasil",
    description: "L'association solidaire que j'ai créée. Actions, sourires, terrain.",
    label: "Le compte solidaire",
    icon: Heart,
    stampColor: "orange",
  },
];

interface Polaroid {
  src: string;
  alt: string;
  caption: string;
  className: string;
  rotate: number;
  delay: number;
}

// Polaroïds latéraux/haut (positionnés là où il n'y a pas de texte).
const sidePolaroids: Polaroid[] = [
  {
    src: socialRio2,
    alt: "Vue aérienne de la plage de Copacabana à Rio au coucher du soleil",
    caption: "Copacabana ☀",
    className: "hidden md:block absolute top-6 right-2 lg:right-8 w-40 lg:w-48",
    rotate: 6,
    delay: 0,
  },
  {
    src: socialRio1,
    alt: "Statue du Christ Rédempteur de Rio à l'heure dorée",
    caption: "Cristo Redentor",
    className: "hidden lg:block absolute top-36 right-44 w-36",
    rotate: -8,
    delay: 0.5,
  },
  {
    src: socialRio6,
    alt: "Cascade dans la forêt tropicale de Tijuca",
    caption: "Tijuca",
    className: "hidden xl:block absolute top-1/2 -translate-y-1/2 left-4 w-40",
    rotate: -6,
    delay: 1,
  },
  {
    src: socialRio3,
    alt: "Téléphérique du Pain de Sucre survolant la baie de Rio",
    caption: "Pão de Açúcar",
    className: "hidden xl:block absolute top-1/2 -translate-y-1/2 right-4 w-40",
    rotate: 7,
    delay: 1.4,
  },
];

const ADMIN_VISIBILITY = [
  "block",
  "hidden sm:block",
  "hidden md:block",
  "hidden lg:block",
  "hidden xl:block",
];

const PolaroidFrame = ({
  src, alt, caption, rotate, className = "", delay = 0,
}: { src: string; alt: string; caption: string; rotate: number; className?: string; delay?: number }) => (
  <div
    className={`${className} pointer-events-none select-none motion-safe:animate-float-soft`}
    style={{ transform: `rotate(${rotate}deg)`, animationDelay: `${delay}s` }}
  >
    <div className="bg-white p-2 pb-7 shadow-[0_15px_35px_-10px_rgba(3,49,77,0.35)]">
      <div className="w-full aspect-square overflow-hidden bg-roca-cream/40">
        <img src={src} alt={alt} loading="lazy" width={768} height={768} className="w-full h-full object-cover" />
      </div>
      <div className="mt-1.5 text-center font-league uppercase text-xs tracking-wider text-roca-blue-dark/70 truncate px-1">
        {caption}
      </div>
    </div>
  </div>
);

const BottomPolaroidRow = () => {
  const { data: adminPolaroids } = useSocialPolaroids();
  const extras = adminPolaroids.slice(0, ADMIN_VISIBILITY.length);
  return (
    <div className="absolute -bottom-6 left-0 right-0 z-0 pointer-events-none">
      <div className="container mx-auto px-2 sm:px-4 max-w-7xl">
        <div className="flex items-end justify-between gap-2 sm:gap-3 md:gap-4">
          <PolaroidFrame src={socialRio4} alt="Marches colorées Selarón à Rio" caption="Escadaria Selarón" rotate={-10} delay={1.8} className="w-28 sm:w-36 md:w-40 lg:w-44 shrink-0" />
          <div className="flex-1 flex items-center justify-center gap-2 sm:gap-3 md:gap-4 min-w-0">
            {extras.map((p, i) => (
              <div key={p.id} className={`${ADMIN_VISIBILITY[i]} shrink-0`}>
                <PolaroidFrame src={p.image_url} alt={p.alt_text || p.caption || "Photo Rio"} caption={p.caption} rotate={i % 2 === 0 ? 5 : -6} delay={1.9 + i * 0.2} className="w-24 sm:w-32 md:w-36 lg:w-40" />
              </div>
            ))}
          </div>
          <PolaroidFrame src={socialRio5} alt="Enfants jouant au football au Brésil" caption="Meninos" rotate={8} delay={2.2} className="w-28 sm:w-36 md:w-40 lg:w-44 shrink-0" />
        </div>
      </div>
    </div>
  );
};

const PostcardCard = ({ acc, index }: { acc: Account; index: number }) => {
  const Icon = acc.icon;
  const stampClass = acc.stampColor === "red" ? "text-roca-red border-roca-red" : "text-roca-orange border-roca-orange";
  const tilt = index % 2 === 0 ? "md:-rotate-1" : "md:rotate-1";
  return (
    <FadeIn direction="up" distance={30} delay={index * 150}>
      <a
        href={acc.href}
        target="_blank"
        rel="noopener noreferrer"
        className={`group relative block h-full bg-white p-6 sm:p-8 md:p-10 transition-all duration-500 ${tilt} hover:rotate-0 hover:-translate-y-1 shadow-[0_8px_30px_-8px_rgba(3,49,77,0.15)] hover:shadow-[0_20px_50px_-12px_rgba(3,49,77,0.25)]`}
        style={{ transformOrigin: "center" }}
      >
        {/* Tampon "ROCA RIO" */}
        <div
          aria-hidden
          className={`absolute top-4 right-4 sm:top-6 sm:right-6 border-2 ${stampClass} px-3 py-1.5 rotate-12 opacity-80 select-none`}
        >
          <div className="font-league uppercase text-sm leading-none tracking-wider flex items-center gap-1.5">
            <Plane className="w-3 h-3" strokeWidth={2.5} />
            <span>Roca Rio</span>
          </div>
          <div className="text-[8px] uppercase tracking-widest mt-0.5 font-montserrat font-semibold opacity-70">
            Rio · Brasil
          </div>
        </div>

        {/* Label */}
        <div className="flex items-center gap-2 mb-6">
          <Icon className={`w-4 h-4 ${acc.stampColor === "red" ? "text-roca-red" : "text-roca-orange"}`} strokeWidth={2} />
          <span className="text-[11px] uppercase tracking-[0.3em] text-roca-blue-dark/60 font-semibold font-montserrat">
            {acc.label}
          </span>
        </div>

        {/* Handle massif */}
        <h3 className="font-league uppercase text-4xl sm:text-5xl md:text-6xl text-roca-blue-dark leading-[0.9] mb-6 break-all">
          {acc.handle}
        </h3>

        {/* Description */}
        <p className="font-montserrat text-roca-blue-dark/75 text-base leading-relaxed mb-8 max-w-md">
          {acc.description}
        </p>

        {/* Bouton Suivre */}
        <div
          className={`inline-flex items-center gap-2 ${
            acc.stampColor === "red" ? "bg-roca-red" : "bg-roca-orange"
          } text-white px-6 py-3 font-montserrat font-semibold text-sm uppercase tracking-wider transition-transform duration-300 group-hover:scale-105`}
        >
          <span>Suivre sur Instagram</span>
          <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
        </div>
      </a>
    </FadeIn>
  );
};

export const HomeSocialSection = () => {
  return (
    <section className="relative py-24 md:py-36 pb-64 md:pb-72 overflow-hidden">
      {/* Texture papier kraft */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] pointer-events-none mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      {/* Polaroïds — positionnés là où il n'y a pas de texte */}
      {sidePolaroids.map((p, i) => (
        <div
          key={i}
          className={`${p.className} pointer-events-none select-none motion-safe:animate-float-soft z-0`}
          style={{
            transform: `rotate(${p.rotate}deg)`,
            animationDelay: `${p.delay}s`,
          }}
        >
          <div className="bg-white p-2 pb-7 shadow-[0_15px_35px_-10px_rgba(3,49,77,0.35)]">
            <div className="w-full aspect-square overflow-hidden bg-roca-cream/40">
              <img
                src={p.src}
                alt={p.alt}
                loading="lazy"
                width={768}
                height={768}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="mt-1.5 text-center font-league uppercase text-xs tracking-wider text-roca-blue-dark/70">
              {p.caption}
            </div>
          </div>
        </div>
      ))}

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl relative z-10">
        {/* En-tête */}
        <FadeIn direction="up" distance={30} className="mb-14 md:mb-20 max-w-xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-league text-xl text-roca-orange">07</span>
            <span className="h-px w-12 bg-roca-orange" />
            <p className="text-xs uppercase tracking-[0.3em] text-roca-blue-dark/60">Le mur des cartes postales</p>
          </div>
          <h2 className="font-league uppercase text-4xl sm:text-5xl md:text-6xl text-roca-blue-dark leading-[0.95] mb-4">
            Suivez l'aventure <span className="text-roca-red">en direct</span>
          </h2>
          <p className="text-lg text-roca-blue-dark/70 font-montserrat">
            Deux comptes, deux univers. Comme deux cartes postales envoyées chaque jour depuis Rio.
          </p>
        </FadeIn>

        {/* Cartes postales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 relative z-10">
          {accounts.map((acc, i) => (
            <PostcardCard key={acc.handle} acc={acc} index={i} />
          ))}
        </div>
      </div>

      <BottomPolaroidRow />
    </section>
  );
};
